import { Content } from '@backstage/core-components';
import { PageShareTitle } from '../../lib/shareTitle';
import { EntityContentBlueprint } from '@backstage/plugin-catalog-react/alpha';
import { DEPLOYMENTS_ANNOTATION } from '../../components/deployments/shared';
import { getStoredFunMode, translate } from '../i18n';

/**
 * Full-page Deployments tab on component entities. Renders the same
 * DeploymentsCard as the overview card (entity-card:catalog/deployments) —
 * the card already shows the full per-environment table plus the expandable
 * promote history, so the tab gives it room without the overview column
 * squeeze. The overview card intentionally stays enabled too.
 *
 * Tab placement (after Overview, before Issues) is pinned by listing
 * entity-content:catalog/deployments in app.extensions in app-config.yaml —
 * ungrouped tabs render in extension spec order, and config-listed
 * extensions move to the front of the spec list in config order (the same
 * frontend-app-api resolveAppNodeSpecs mechanism the overview cards use).
 * Kept in sync by deploymentsEntityContent.test.ts.
 */
export const deploymentsEntityContent = EntityContentBlueprint.make({
  name: 'deployments',
  params: {
    path: '/deployments',
    // Non-component config (evaluated once at extension registration), so
    // this reads the fun-mode flag directly instead of the useT() hook — see
    // the i18n module's non-component-code note.
    title: translate(
      'catalog.deploymentsEntityContent.title',
      getStoredFunMode(),
    ),
    // Predicate form of the config-style filter used by
    // entity-content:github-issues/entity in app-config.yaml: only components
    // that actually carry deployment data get the tab (the card filter is
    // broader — it renders a "No deployment data" placeholder on the
    // overview, but an entire empty tab would be noise).
    filter: {
      kind: 'component',
      [`metadata.annotations.${DEPLOYMENTS_ANNOTATION}`]: { $exists: true },
    },
    loader: () =>
      import('../../components/deployments/DeploymentsCard').then(m => (
        <PageShareTitle title="Deployments">
          <Content>
            <m.DeploymentsCard />
          </Content>
        </PageShareTitle>
      )),
  },
});
